import { useRef, useState } from 'react';
import { Loader2, Upload, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import { VisibilityField } from 'components/blocks/visibility-field';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { RESOURCE_VISIBILITY, type ResourceVisibility } from 'types/visibility';
import { useImportSkill } from './use-skills';

const ACCEPTED_FILE_EXTENSIONS = ['.md', '.zip'];
const SKILL_MARKDOWN_FILENAME = 'SKILL.md';
const MAX_DESCRIPTION_LENGTH = 1024;

// Mirrors the backend's frontmatter contract (skill_authoring_service.py:
// _FRONTMATTER_RE / _frontmatter_to_create_request) so obviously malformed
// files are rejected before a round trip. The server remains the source of
// truth for full YAML validation.
const FRONTMATTER_PATTERN = /^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*\r?\n?([\s\S]*)$/;

type SkillMarkdownFormatResult =
  | { valid: true }
  | { valid: false; reasonKey: string; reasonDefault: string };

const extractFrontmatterField = (frontmatter: string, key: string): string | undefined => {
  const match = new RegExp(`(?:^|\\n)${key}\\s*:\\s*(.*)`, 'i').exec(frontmatter);
  const value = match?.[1]?.trim().replace(/^['"]|['"]$/g, '');
  return value || undefined;
};

const BOM_PATTERN = /^\uFEFF/;

const checkSkillMarkdownFormat = (text: string): SkillMarkdownFormatResult => {
  const match = FRONTMATTER_PATTERN.exec(text.replace(BOM_PATTERN, ''));
  if (!match) {
    return {
      valid: false,
      reasonKey: 'IMPORT_SKILL_MISSING_FRONTMATTER',
      reasonDefault: 'File must start with a YAML frontmatter block (--- ... ---).',
    };
  }

  const [, frontmatter] = match;
  const name = extractFrontmatterField(frontmatter, 'name') || extractFrontmatterField(frontmatter, 'slug');
  if (!name) {
    return {
      valid: false,
      reasonKey: 'IMPORT_SKILL_MISSING_NAME',
      reasonDefault: 'Frontmatter is missing a "name" (or "slug").',
    };
  }

  const description = extractFrontmatterField(frontmatter, 'description');
  if (!description) {
    return {
      valid: false,
      reasonKey: 'IMPORT_SKILL_MISSING_DESCRIPTION',
      reasonDefault: 'Frontmatter is missing a "description".',
    };
  }

  if (description.length > MAX_DESCRIPTION_LENGTH) {
    return {
      valid: false,
      reasonKey: 'IMPORT_SKILL_DESCRIPTION_TOO_LONG',
      reasonDefault: `Frontmatter "description" must be ${MAX_DESCRIPTION_LENGTH} characters or fewer.`,
    };
  }

  return { valid: true };
};

const renameToSkillMarkdown = (file: File): File =>
  new File([file], SKILL_MARKDOWN_FILENAME, {
    type: file.type || 'text/markdown',
    lastModified: file.lastModified,
  });

const getErrorDetailMessage = (error: unknown) => {
  if (error && typeof error === 'object') {
    const maybeError = error as {
      error?: unknown;
      detail?: unknown;
      message?: unknown;
    };

    if (maybeError.error && typeof maybeError.error === 'object') {
      const nestedError = maybeError.error as { detail?: unknown };
      if (typeof nestedError.detail === 'string' && nestedError.detail.trim()) {
        return nestedError.detail;
      }
    }

    if (typeof maybeError.detail === 'string' && maybeError.detail.trim()) {
      return maybeError.detail;
    }

    if (typeof maybeError.message === 'string' && maybeError.message.trim()) {
      return maybeError.message;
    }
  }

  return '';
};

const isAllowedSkillFile = (file: File) => {
  const normalizedName = file.name.trim().toLowerCase();
  return ACCEPTED_FILE_EXTENSIONS.some((extension) => normalizedName.endsWith(extension));
};

const isZipFile = (file: File) => file.name.trim().toLowerCase().endsWith('.zip');

type ImportSkillModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ImportSkillModal({ open, onOpenChange }: ImportSkillModalProps) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [visibility, setVisibility] = useState<ResourceVisibility>(RESOURCE_VISIBILITY.PUBLIC);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { toast } = useToast();
  const { mutateAsync: importSkill, isPending } = useImportSkill();

  const resetState = () => {
    setSelectedFile(null);
    setIsDragActive(false);
    setVisibility(RESOURCE_VISIBILITY.PRIVATE);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      resetState();
    }
  };

  const selectFile = async (fileList: FileList | null) => {
    if (!fileList?.length) {
      return;
    }

    const nextFile = Array.from(fileList).find(isAllowedSkillFile);
    if (!nextFile) {
      toast({
        title: t('UNSUPPORTED_FILE') || 'Unsupported file',
        description:
          t('IMPORT_SKILL_UNSUPPORTED_FILES') ||
          'Only .md and .zip files can be imported here.',
        variant: 'destructive',
      });
      return;
    }

    if (isZipFile(nextFile)) {
      setSelectedFile(nextFile);
      return;
    }

    let text: string;
    try {
      text = await nextFile.text();
    } catch {
      toast({
        title: t('UNSUPPORTED_FILE') || 'Unsupported file',
        description: t('IMPORT_SKILL_UNREADABLE_FILE') || 'The file could not be read.',
        variant: 'destructive',
      });
      return;
    }

    const formatResult = checkSkillMarkdownFormat(text);
    if (!formatResult.valid) {
      toast({
        title: t('INVALID_SKILL_FILE') || 'Invalid SKILL.md',
        description: t(formatResult.reasonKey, { defaultValue: formatResult.reasonDefault }),
        variant: 'destructive',
      });
      return;
    }

    setSelectedFile(nextFile);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      return;
    }

    try {
      const file = isZipFile(selectedFile) ? selectedFile : renameToSkillMarkdown(selectedFile);
      const response = await importSkill({ file, visibility });
      toast({
        variant: 'success',
        title: t('SKILL_IMPORTED') || 'Skill imported',
        description:
          response.detail ||
          response.message ||
          t('SKILL_IMPORTED_SUCCESSFULLY', {
            name: selectedFile.name,
            defaultValue: '{{name}} was imported successfully.',
          }),
      });
      handleOpenChange(false);
    } catch (error) {
      toast({
        title: t('FAILED_TO_IMPORT_SKILL') || 'Failed to import skill',
        description: getErrorDetailMessage(error) || t('PLEASE_TRY_AGAIN') || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-[520px] rounded-xl p-0 shadow-xl" hideClose>
        <div className="px-6 py-5">
          <DialogHeader className="space-y-3 pr-0">
            <div className="flex items-start justify-between gap-4">
              <DialogTitle className="text-[18px] font-semibold text-foreground">
                {t('IMPORT_SKILL') || 'Import Skill'}
              </DialogTitle>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5 shrink-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
                onClick={() => handleOpenChange(false)}
              >
                <span className="sr-only">{t('CLOSE') || 'Close'}</span>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <DialogDescription className="text-[15px] leading-7 text-muted-foreground">
              {t('IMPORT_SKILL_DESCRIPTION') ||
                'Upload a .md file (imported as SKILL.md), or import a .zip skill archive. The file must include YAML frontmatter with a name and description.'}
            </DialogDescription>
          </DialogHeader>

          <label
            className={`mt-4 flex min-h-[168px] w-full cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed px-6 py-8 text-center transition-colors ${isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/20'
              }`}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void selectFile(event.dataTransfer.files);
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".md,.zip"
              className="hidden"
              onChange={(event) => void selectFile(event.target.files)}
            />
            <Upload className="mb-5 h-6 w-6 text-muted-foreground" />
            <div className="max-w-[360px] space-y-3">
              <p className="text-[15px] font-medium leading-6 text-foreground">
                {selectedFile
                  ? t('SELECTED_FILE', {
                    name: selectedFile.name,
                    defaultValue: 'Selected file: {{name}}',
                  })
                  : t('IMPORT_SKILL_DROP_ZONE') ||
                  'Drag & drop a .md or .zip file, or click to select'}
              </p>
              <p className="text-sm leading-6 text-muted-foreground">
                {t('IMPORT_SKILL_FILE_TYPES_HINT') ||
                  'Single .md file (imported as SKILL.md), or a .zip skill archive. Unsupported file types are ignored.'}
              </p>
            </div>
          </label>

          <VisibilityField
            className="mt-4"
            value={visibility}
            onValueChange={setVisibility}
            idPrefix="import-skill-visibility"
            disabled={isPending}
          />
        </div>

        <DialogFooter className="border-t border-border px-6 py-4">
          <div className="flex w-full items-center justify-end gap-2">
            <Button type="button" variant="ghost" className="h-9 px-4" onClick={() => handleOpenChange(false)}>
              {t('CANCEL') || 'Cancel'}
            </Button>
            <Button type="button" className="h-9 px-4" disabled={!selectedFile || isPending} onClick={handleImport}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isPending ? t('IMPORTING') || 'Importing...' : t('IMPORT') || 'Import'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
